import { at, isNil, isNumber, max } from 'lodash-es';
import { createSelector } from 'reselect';

import { Timecode } from '@iconik-io/core/src/timecode';
import type { AssetsAssetSchemaTypedWithId } from '@iconik-io/iconik-api/src/services/assets';
import type { AppPlaylistItem } from '@iconik-io/shared-lib/src/react/components/shared/player/context';
import {
  getPlaylistItemVariants,
  selectPlayerContentType,
  selectSubclipUsesCustomProxy,
} from '@iconik-io/shared-lib/src/react/components/shared/player/playlist_items';
import { createUnifiedSegment } from '@iconik-io/shared-lib/src/react/components/shared/player/segments';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { selectAssetOrSearchItem } from '@iconik-io/shared-lib/src/redux/entities/selectors/assets';
import {
  selectFilesByAssetId,
  selectOriginalFilesByVersionIdMap,
} from '@iconik-io/shared-lib/src/redux/entities/selectors/files';
import {
  selectAssetOriginalFormat,
  selectOriginalFormatsByVersionIdMap,
} from '@iconik-io/shared-lib/src/redux/entities/selectors/formats';
import { selectKeyframesByVersionIdMap } from '@iconik-io/shared-lib/src/redux/entities/selectors/keyframes';
import { getDefaultProxy } from '@iconik-io/shared-lib/src/redux/entities/selectors/proxies';
import {
  selectAssetVersionsExtendedMap,
  selectCurrentVersionId,
} from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import { languagesMap } from '@iconik-io/shared-lib/src/utils/constants/files';
import {
  COLOR_CODING,
  MARKER_LANE_TYPE_SUBCLIP,
} from '@iconik-io/shared-lib/src/utils/constants/player';
import { zeroTimecode } from '@iconik-io/shared-lib/src/utils/constants/timecode';
import { findBestKeyframe } from '@iconik-io/shared-lib/src/utils/find_best_keyframe';
import { getOriginalFormatTimecodeOptions } from '@iconik-io/shared-lib/src/utils/get_original_format_timecode_options';
import { getProxyTimecodeOptions } from '@iconik-io/shared-lib/src/utils/get_proxy_timecode_options';
import { resolveMediaUrl } from '@iconik-io/shared-lib/src/utils/resolve_media_url';
import type { Segment, Subtitle } from '@iconik-io/unified-player-core';

import {
  COMMENTS_KEY,
  FACE_RECOGNITION,
  SUBCLIPS_KEY,
  TIME_BASED_METADATA_KEY,
  TRANSCRIPTIONS_KEY,
} from '../../constants/data';
import { selectAssetCommentIds } from '../../ducks/asset_comments/selectors';
import { selectTranscriptionSegmentsByAssetId } from '../../ducks/asset_transcriptions/selectors';
import {
  selectAssetProxy,
  selectPlayableAssetProxiesForVersion,
  selectSubclipAssets,
} from '../../ducks/pages/asset/selectors';
import { getSubtitles } from '../../panels/asset_files/selectors';
import { selectSegmentIds } from '../../panels/asset_time_based_metadata/selectors';
import type { AppState } from '../../store/types';

export const getPlayOriginalDuringTranscoding = (state: AppState) =>
  Boolean(state.settings.merged.allow_play_original_during_transcoding);

export const selectPlaylistItemByAssetId = createSelector(
  selectAssetOrSearchItem,
  selectPlayableAssetProxiesForVersion,
  selectOriginalFormatsByVersionIdMap,
  selectOriginalFilesByVersionIdMap,
  selectAssetOriginalFormat,
  selectFilesByAssetId,
  selectKeyframesByVersionIdMap,
  selectAssetVersionsExtendedMap,
  selectCurrentVersionId,
  selectSubclipUsesCustomProxy,
  getPlayOriginalDuringTranscoding,
  (
    asset,
    assetProxies,
    originalFormatsByVersionIdMap,
    originalFilesByVersionIdMap,
    unscopedOriginalFormat,
    files,
    keyframesByVersionIdMap,
    versionsExtendedMap,
    versionId,
    subclipUsesCustomProxy,
    playOriginalDuringTranscoding,
  ): AppPlaylistItem | undefined => {
    if (!asset) {
      return undefined;
    }

    const assetProxy = getDefaultProxy(assetProxies);
    const version = versionId ? versionsExtendedMap[versionId] : undefined;

    // An asset whose versions we don't know about yet can't be version-scoped,
    // so it keeps the previous behavior of taking whichever original we hold
    const originalFormat = versionId
      ? originalFormatsByVersionIdMap[versionId]
      : unscopedOriginalFormat;

    let originalFile = versionId
      ? originalFilesByVersionIdMap[versionId]
      : files.find((file) => file.format_id === originalFormat?.id);

    const assetProxyReady = Boolean(assetProxy);

    const contentType = selectPlayerContentType(assetProxy, originalFormat);
    // TODO(FE-7071): Remove the proxy-less PDF exemption and the ISG original
    // file URL substitution below once PDFs are rendered from proxies only and
    // originals are not loaded for viewing.
    if (
      !assetProxyReady &&
      (!originalFile || !playOriginalDuringTranscoding) &&
      contentType !== 'pdf'
    ) {
      return undefined;
    }

    // PDFs on ISG don't have a URL unless copied to cloud storage
    if (contentType === 'pdf' && originalFile && !originalFile.url) {
      const originalFilePdf = files.find(
        (file) => file.format_id === originalFormat?.id && file.url,
      );
      originalFile = { ...originalFile, url: originalFilePdf?.url };
    }

    const url = resolveMediaUrl(assetProxy, originalFile, contentType);

    const naturalSize =
      assetProxy?.resolution?.height && assetProxy?.resolution?.width
        ? {
            height: assetProxy.resolution.height,
            width: assetProxy.resolution.width,
          }
        : undefined;

    if (!contentType) {
      return undefined;
    }

    const name = `${asset.title}${version ? ` ${version.name}` : ''}`;

    const timecodeOptions = assetProxy
      ? getProxyTimecodeOptions(assetProxy)
      : getOriginalFormatTimecodeOptions(originalFormat, contentType);

    if (!timecodeOptions) {
      return undefined;
    }

    let playableRange;
    if (
      asset.type === 'SUBCLIP' &&
      isNumber(asset.time_start_milliseconds) &&
      isNumber(asset.time_end_milliseconds) &&
      !subclipUsesCustomProxy
    ) {
      playableRange = {
        start: asset.time_start_milliseconds / 1000,
        end: asset.time_end_milliseconds / 1000,
      };
    }

    const duration = max(
      originalFormat?.components?.map(
        (component) => Number(component.metadata?.duration) / 1000,
      ),
    );

    let firstFrameTimecode = timecodeOptions.startTimecode;

    if (subclipUsesCustomProxy && isNumber(asset.time_start_milliseconds)) {
      const startTimecode = new Timecode(timecodeOptions, {
        smpte: assetProxy?.start_time_code ?? zeroTimecode,
      });

      const timecode = new Timecode(timecodeOptions, {
        seconds:
          startTimecode.toSeconds() + asset.time_start_milliseconds / 1000,
      });

      firstFrameTimecode = timecode.toSMPTE();
    }

    // Different assets can have the same proxy (e.g. in subclips).
    // Same asset can have multiple versions and each version would be a different proxy.
    // To handle this we make playlist id unique by combining asset id and proxy id.
    const playlistId = [asset.id, (assetProxy ?? originalFile)?.id].join('_');

    let thumbnailUrl;
    if (versionId) {
      thumbnailUrl =
        findBestKeyframe(keyframesByVersionIdMap[versionId])?.url ?? undefined;
    }

    const playlistItem: AppPlaylistItem = {
      id: playlistId,
      contentType,
      url,
      name,
      frameRate: timecodeOptions.frameRate,
      isDropFrame: timecodeOptions.isDropFrame,
      isGrowing: assetProxy?.status === 'GROWING',
      playableRange,
      duration,
      naturalSize,
      firstFrameTimecode,
      thumbnailUrl,
      availableVariants: getPlaylistItemVariants(assetProxies, originalFile),
      meta: {
        assetId: asset.id,
        versionId,
      },
    };

    return playlistItem;
  },
);

export const selectUnifiedSubtitles = createSelector(
  getSubtitles,
  (subtitles): Subtitle[] => {
    const subtitlesArray = subtitles.map((subtitle) => ({
      id: subtitle.id,
      url: subtitle.url,
      language: subtitle.language,
      name:
        languagesMap.get(subtitle.language)?.label ||
        subtitle.name ||
        subtitle.id,
    }));
    return subtitlesArray;
  },
);

const getIsActiveCommentsPanel = (state: AppState): boolean =>
  state.panels.openedPanels[COMMENTS_KEY];

const getIsActiveTimeBasedMetadataPanel = (state: AppState): boolean =>
  state.panels.openedPanels[TIME_BASED_METADATA_KEY];

const getIsActiveTranscriptionsPanel = (state: AppState): boolean =>
  state.panels.openedPanels[TRANSCRIPTIONS_KEY];

const getIsActiveSubclipsPanel = (state: AppState): boolean =>
  state.panels.openedPanels[SUBCLIPS_KEY];

const getIsActiveFaceRecognitionPanel = (state: AppState): boolean =>
  state.panels.openedPanels[FACE_RECOGNITION];

const getPersonSegmentIds = (state: AppState) =>
  state.panelsState.assetPersonSegments.ids;

const getSegmentEntities = (state: AppState) => state.entities.segments;

const transcriptionSegmentColors = [
  // --color-palette-primary-green
  '#a6d387',
  // --color-palette-primary-indigo
  '#a1a5f5',
  // --color-palette-primary-red
  '#ff8881',
  // --color-palette-primary-orange
  '#e79d65',
];

const createSubclipSegment = (
  subclipAsset: SearchItemEntity | AssetsAssetSchemaTypedWithId,
): Segment | null => {
  const color = COLOR_CODING.subclips;
  const startMs = subclipAsset.time_start_milliseconds ?? undefined;
  const endMs = subclipAsset.time_end_milliseconds ?? undefined;
  if (startMs === undefined || endMs === undefined) {
    return null;
  }

  return {
    id: subclipAsset.id,
    type: MARKER_LANE_TYPE_SUBCLIP,
    color,
    range: {
      start: startMs / 1000,
      end: endMs / 1000,
    },
  };
};

export const selectUnifiedSegments = createSelector(
  [
    getIsActiveCommentsPanel,
    getIsActiveTimeBasedMetadataPanel,
    getIsActiveTranscriptionsPanel,
    getIsActiveSubclipsPanel,
    getSegmentEntities,
    selectSegmentIds,
    selectAssetCommentIds,
    selectSubclipAssets,
    selectTranscriptionSegmentsByAssetId,
    getIsActiveFaceRecognitionPanel,
    selectAssetProxy,
    selectAssetOrSearchItem,
    getPersonSegmentIds,
  ],
  (
    isActiveCommentsPanel,
    isActiveTimeBasedMetadataPanel,
    isActiveTranscriptionsPanel,
    isActiveSubclipsPanel,
    segmentEntities,
    segmentsIds,
    commentsIds,
    subclipAssets,
    transcriptions,
    isActiveFacialRecognitionPanel,
    assetProxy,
    asset,
    personSegmentIds,
  ) => {
    const timeBasedMetadata = at(segmentEntities, segmentsIds);
    const comments = at(segmentEntities, commentsIds);
    const persons = at(segmentEntities, personSegmentIds);

    let segments: SegmentEntityWithIdNormalized[] = [];

    if (isActiveCommentsPanel) {
      segments = segments.concat(comments);
    }

    if (isActiveFacialRecognitionPanel) {
      segments = segments.concat(persons);
    }

    if (isActiveTimeBasedMetadataPanel) {
      segments = segments.concat(timeBasedMetadata);
    }

    if (isActiveTranscriptionsPanel) {
      const transcriptionsWithColors = transcriptions.map(
        (transcriptionSegment) => ({
          ...transcriptionSegment,
          segment_color:
            transcriptionSegment.transcription &&
            !isNil(transcriptionSegment.transcription.speaker)
              ? transcriptionSegmentColors[
                  transcriptionSegment.transcription.speaker
                ]
              : undefined,
        }),
      );
      segments = segments.concat(transcriptionsWithColors);
    }

    const result: Segment[] = segments.map((segment) =>
      createUnifiedSegment(segment, segments, assetProxy, asset),
    );

    if (isActiveSubclipsPanel) {
      const subclipSegments = subclipAssets
        .map(createSubclipSegment)
        .filter((segment): segment is Segment => segment !== null);
      result.push(...subclipSegments);
    }

    return result;
  },
);

export const selectSelectedSegments = createSelector(
  (state: AppState) => state.entities.segments,
  (_state: AppState, selectedSegmentIds: readonly string[]) =>
    selectedSegmentIds,
  (segments, selectedSegmentIds) =>
    selectedSegmentIds
      .map((id) => segments[id])
      .filter((segment) => segment !== undefined),
);
